import React, { lazy, Suspense } from 'react';
import { Activity, BarChart3 } from 'lucide-react';

const WorkoutChart = lazy(() => import('@/features/workout-generator/components/WorkoutChart'));
import type { AIWorkoutResponse, SportType, ZoneDefinition, RunIntensity, WorkoutSegment } from '@/types';
import type { TranslationKeys } from '@/core/i18n/translations';

type Theme = 'light' | 'dark';
type Lang = 'en' | 'pl';

export interface WorkoutVisualizerColumnProps {
  t: TranslationKeys;
  theme: Theme;
  language: Lang;
  sport: SportType;
  runIntensity: RunIntensity;
  activeZones: ZoneDefinition[];
  segments: WorkoutSegment[];
  /** Gdy ustawione, wykres liczy segmenty z `normalizeParsedWorkout` + processor (spójnie z API). */
  aiWorkout?: AIWorkoutResponse | null;
  workoutName: string;
  isLoading: boolean;
}

export function WorkoutVisualizerColumn({
  t,
  theme,
  language,
  sport,
  runIntensity,
  activeZones,
  segments,
  aiWorkout,
  workoutName,
  isLoading,
}: WorkoutVisualizerColumnProps) {
  const hasAiSteps = Array.isArray(aiWorkout?.steps) && aiWorkout.steps.length > 0;
  const showChart = segments.length > 0 || hasAiSteps;

  return (
    <section className="bg-white p-4 rounded-2xl border border-blue-200 shadow-xl backdrop-filter backdrop-blur-lg bg-opacity-80 dark:bg-slate-900 dark:border-slate-700 dark:bg-opacity-10">
      <h2 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
        <Activity className="w-5 h-5 text-blue-500" />
        {t.visualizerTitle}
      </h2>
      <div className="h-[280px] min-h-0">
        {showChart ? (
          <Suspense
            fallback={
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                {isLoading ? t.generatingBtn : '…'}
              </div>
            }
          >
            <WorkoutChart
              key={workoutName + segments.length + (aiWorkout?.steps?.length ?? 0)}
              segments={segments}
              aiWorkout={aiWorkout ?? undefined}
              zones={activeZones}
              theme={theme}
              sport={sport}
              runIntensity={runIntensity}
              language={language}
            />
          </Suspense>
        ) : (
          <div className="h-full flex flex-col items-center justify-center border-2 border-dashed rounded-lg text-slate-400 gap-3 px-4 text-center">
            <BarChart3
              className="w-16 h-16 text-gray-200 dark:text-slate-700"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <span className="text-sm">
              {isLoading ? t.generatingBtn : t.visualizerEmpty}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
